/**
 * @file     SellOffMarketLink.tsx
 * @purpose  The one seller-hub entry to /sell-off-market, rendered under the shared seller forms so every hub routes quiet-sale intent to the single off-market flow instead of growing its own form.
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { routes } from '@/core/constants';
import { useAnalytics } from '@/hooks/useAnalytics';
import { OFF_MARKET_AREAS } from '@/features/lead-flows/configs/sellerOffMarketFlow';

const toCityId = (city?: string) => city?.trim().toLowerCase().replace(/\s+/g, '-');

interface Props {
  sourcePage: string;
  /** City slug or display name of the hub; preselects the quiz when covered. */
  city?: string;
}

export function SellOffMarketLink({ sourcePage, city }: Props) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const c = theme.semanticColors;
  const { trackCtaClick } = useAnalytics();
  const cityId = toCityId(city);
  const href = cityId && Object.hasOwn(OFF_MARKET_AREAS, cityId) ? `${routes.sellOffMarket}?city=${cityId}` : routes.sellOffMarket;

  return (
    <Box
      component={Link}
      href={href}
      onClick={() => trackCtaClick('sell_off_market_hub_link', sourcePage, href)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        minHeight: 52,
        mt: 2,
        pt: 2,
        borderTop: `1px solid ${c.divider}`,
        color: c.textSecondary,
        textDecoration: 'none',
        fontSize: '0.875rem',
        lineHeight: 1.45,
        transition: 'color 150ms',
        '&:hover': { color: c.textPrimary },
        '&:hover .sell-off-market-arrow': { transform: 'translateX(3px)' },
        '&:focus-visible': { outline: `2px solid ${theme.rawSemanticColors.interactivePrimary}`, outlineOffset: 2, borderRadius: `${theme.layout.radius.sm}px` },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '& .sell-off-market-arrow': { transition: 'none' } },
      }}
    >
      <span>
        {t('features.sellOffMarketLink.prompt', { defaultValue: 'Rather sell without a sign on the lawn?' })}{' '}
        <Box component="span" sx={{ color: c.interactivePrimary, fontWeight: 500 }}>
          {t('features.sellOffMarketLink.cta', { defaultValue: 'See how an off-market sale works' })}
        </Box>
      </span>
      <Box
        component="span"
        aria-hidden="true"
        className="sell-off-market-arrow"
        sx={{ display: 'inline-flex', flexShrink: 0, color: c.interactivePrimary, transition: 'transform 150ms' }}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </Box>
    </Box>
  );
}
